import { Component, OnInit, forwardRef, Output, Input, EventEmitter } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import * as _ from 'lodash';
import { UiService } from '../../core/services/core.ui.service';

const noop = () => {};

const TAGS_INPUT_TEMPLATE = `
    <div class="tags-input">
        <ng-container *ngFor="let tag of tags">
            <span class="tags-input__tag label label-primary tag-input-pill" [ngStyle]="{ 'background-color': tag.bgcolor }">
                {{tag[displayField]}}
                <span *ngIf="isDeleteable(tag)" role="button" class="tags-input__tag-remove-btn" (click)="removeTag(tag)" (touch)="removeTag(tag)">
                    <span aria-hidden="true">&times;</span>
                    <span class="sr-only">Close</span>
                </span>
            </span>
        </ng-container>
        <input
            *ngIf="options === null"
            class="tags-input__input-field"
            type="text"
            placeholder="{{ getPlaceHolder() }}"
            name="tags"
            (keyup.enter)="addTag(tagInput, $event)" (keydown.backspace)="removeLastTag(tagInput)"
            (keydown)="onInputKeydown(tagInput, $event)"
            [disabled]="maximumOfTagsReached()"
            [hidden]="maximumOfTagsReached()"
            #tagInput />
        <input
            *ngIf="options !== null"
            class="tags-input__input-field"
            type="text"
            placeholder="{{ getPlaceHolder() }}"
            name="tags"
            (keydown.backspace)="removeLastTag(tagInput)"
            (keydown)="onInputKeydown(tagInput, $event)"
            [(ngModel)]="selected" 
            [typeahead]="options"
            [typeaheadOptionField]="displayField"
            (typeaheadOnSelect)="typeaheadOnSelect($event)"
            (typeaheadNoResults)="typeaheadOnNoMatch($event)"
            [typeaheadMinLength]="minLengthBeforeOptions"
            [typeaheadScrollable]="scrollableOptions"
            [typeaheadOptionsInScrollableView]="scrollableOptionsInView"
            [disabled]="maximumOfTagsReached()"
            [hidden]="maximumOfTagsReached()"
            #tagInput />
    </div>
`;

const TAGS_INPUT_STYLE = `
    :host {
        overflow: hidden;
        white-space: nowrap;
        height: fit-content;
    }

    .tags-input {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
    }

    .tags-input__tag {
        display: inline-block;
        margin-bottom: 2px;
        margin-right: 5px;
        padding-right: 0.3em;
    }

    .tags-input__tag-remove-btn {
        cursor: pointer;
        display: inline-block;
        font-size: 12px;
        margin: -3px 0 0 3px;
        padding: 0;
        vertical-align: top;
    }

    .tags-input__input-field {
        border: none;
        flex-grow: 1;
        font-size: small;
        outline: none;
    }

    .tag-input-pill {
        font-size: small;
    }
`;

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TagsInputComponent),
    multi: true
};

@Component({
  selector: 'gf-tags-input',
  template: TAGS_INPUT_TEMPLATE,
  styles: [TAGS_INPUT_STYLE],
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class TagsInputComponent implements OnInit, ControlValueAccessor {
    protected selected:string = '';
    public tags: any[] = [];
    private onTouchedCallback: () => void = noop;
    private onChangeCallback: (_: any) => void = noop;

    @Input() maxTags: number;
    @Input() removeLastOnBackspace: boolean = false;
    @Input() canDeleteTags: boolean = true;
    @Input() placeholder: string = '';
    @Input() options: any = null;
    @Input() displayField: string = 'text';
    @Input() minLengthBeforeOptions: number = 1;
    @Input() scrollableOptions: boolean = false;
    @Input() scrollableOptionsInView: number = 5;

    @Output() onTagsChanged = new EventEmitter();
    @Output() onMaxTagsReached = new EventEmitter();
    @Output() onNoOptionsMatch = new EventEmitter();

    constructor(
        private uiService: UiService,
    ) {}

    ngOnInit() {}

    protected getPlaceHolder(): string {
        if(this.tags && this.tags.length > 0){
            return '';
        }
        return this.placeholder;
    }

    private tagsChanged(type: string, tag: any): void {
        this.onChangeCallback(this.tags);
        this.onTagsChanged.emit(
            (_.map(this.tags, (item) => { return item.text; })).join(',')
        );
        if(this.maximumOfTagsReached()){
            this.onMaxTagsReached.emit();
        }
    }

    protected removeLastTag(tagInput: HTMLInputElement): void {
        if(!this.removeLastOnBackspace || !this.tags.length) {
            return;
        }

        if (tagInput.value === ''){
            this.removeTag(this.tags[this.tags.length-1]);
        }
    }

    protected onInputKeydown(tagInput: HTMLInputElement, event: KeyboardEvent): void {
        if (event.key !== ',') return;
        event.preventDefault();
        this.addTag(tagInput, event);
    }

    protected addTag(tagInput: HTMLInputElement, event: Event): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (tagInput.value.trim() !== '') {
            const bgcolor = this.uiService.color.stringToClassColor(tagInput.value);
            let tag = {
                [this.displayField]: tagInput.value,
                bgcolor: bgcolor
            };
            this.addPredefinedTag(tag);
        }
        tagInput.value = '';
    }

    private addPredefinedTag(tag: Object): void {
        if (!this.maximumOfTagsReached()){
            this.tags.push(tag);
            this.tagsChanged('add', tag);
        }
    }

    protected removeTag(tagToRemove: any): void {
        if(!this.isDeleteable(tagToRemove)){
            return;
        }
        this.tags = this.tags.filter(tag => tagToRemove !== tag);
        this.tagsChanged('remove', tagToRemove);
    }

    protected maximumOfTagsReached(): boolean {
        return typeof this.maxTags !== 'undefined' && this.tags.length>=this.maxTags;
    }

    protected isDeleteable(tag: any) {
        if(typeof tag.deleteable !== "undefined" && !tag.deleteable){
            return false;
        }
        return this.canDeleteTags;
    }

    protected typeaheadOnSelect(e:TypeaheadMatch):void {
        if(typeof e.item === 'string'){
            this.addPredefinedTag({
                [this.displayField]: e.value
            });
        }else {
            this.addPredefinedTag(e.item);
        }
        this.selected = '';
    }

    protected typeaheadOnNoMatch(e:any):void {
        if(typeof this.onNoOptionsMatch !== 'undefined'){
            this.onNoOptionsMatch.emit(e)
        }
    }

    writeValue(value: any) {
        if (value !== this.tags) {
            this.tags = this.uiService.color.setTagColors(value);
        }
    }

    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }
}
